import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { PricingDetail, InstantEpriceContainer } from 'wsm-pricing-display';
import { useDealerOptions } from 'wsm-pricing-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import { ButtonList } from 'wsm-vehicle-cta-display';
import { Tabs, Tab, Collapse } from 'react-bootstrap';
import { useRequestData, usePrefs, useLabels, useFlags } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { DetailedIncentives, filterIncentiveIds } from 'wsm-incentive-display';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import logger from 'ws-scripts/modules/logger';
import { isInTransit } from 'wsm-highlighted-attribute';
import HighlightedAttributeContainer from './HighlightedAttributeContainer';
import VehicleAttributeList from './VehicleAttributeList';
import Callout from '../Callout';
import VehiclePackages from './VehiclePackages';
import { getVehicleObj } from '../../../utilities/vehicleTracking';
import { pricingMap } from '../../../utilities/pricingMap';
import {
	getTargetLocations,
	getVehicleLocationName
} from '../../../utilities/getTargetLocations';
import {
	selectIsGridLayout,
	selectIsTabbedLayout
} from '../../../utilities/layout';
import ConditionalWrapper from '../../ConditionalWrapper';
import LocationDistance from './LocationDistance';
import { checkConcaternateAttr } from '../../../utilities/concatenateAttributeCheck';
import WindowSticker from './WindowSticker';
import { hasInTransitBadge } from '../../../utilities/hasInTransitBadge';
import WebIntegrationPlaceholder from '../../WebIntegrationPlaceholder';

const VehicleCardData = ({
	accountId,
	accountInfo,
	incentives,
	vehicle,
	highlightedAttributes,
	isDisplayingEPrice,
	setIsDisplayingEPrice,
	showExpandedPricing,
	setShowExpandedPricing,
	showNewCarBoostPaymentsInVehicleCardData
}) => {
	const {
		buttonSizeEPrice,
		buttonStyleEPrice,
		ePriceFontWeight,
		incentiveDisplayGrouping,
		incentiveTitleCustom,
		incentiveTitleDisplay,
		instantEpricePreventMultipleEpriceLeads,
		instantEpriceShowAllAfterLeadSubmit,
		restrictInsertLocationsForVehicles,
		showDealerConditionalIncentives,
		showDealerRegularIncentives,
		showIncentives,
		showInstantEpriceForSharedInventory,
		showOemConditionalIncentives,
		showOemNationalIncentives,
		showPackages,
		tabInfoLabel,
		tabOrder,
		tabPricingLabel,
		tabSpecialsLabel,
		hideInformationTabs,
		showInformationTabs,
		showSRPWindowSticker
	} = usePrefs();

	const {
		accountId: vehicleAccountId,
		attributes = [],
		bestIncentiveIds = [],
		callout: callouts,
		classification,
		condition,
		images,
		incentiveIds = [],
		inventoryButtons = [],
		link,
		packages = [],
		pricing = {},
		status,
		title,
		type,
		uuid
	} = vehicle;

	const locationDistanceObj = attributes.find(
		(item) => item.name === 'locationDistance'
	);

	let locationDistanceLabel = locationDistanceObj?.labeledValue;

	if (!checkConcaternateAttr('locationDistance', locationDistanceLabel)) {
		locationDistanceLabel = locationDistanceObj?.value;
	}

	const isTabbedLayout = useSelector((state) => selectIsTabbedLayout(state));
	const isGridLayout = useSelector((state) => selectIsGridLayout(state));
	// TODO: remove mapping here (and in ws-detailed-pricing) and update wsm-pricing-display to use lowercase version once GVM databus is retired
	const componentPricing = pricingMap(pricing);

	const incentiveIdList = incentiveIds || bestIncentiveIds || [];

	const [shouldRequestDealerOptions, setShouldRequestDealerOptions] =
		useState(false);
	const { userProfile } = useSelector((state) => state);
	const { pageAlias } = useSelector((state) => state.widgetInfo);
	const { useSimplifiedListing } = useSelector((state) => state.layout);
	const { isSmallScreen } = useSelector((state) => state.layout);

	const { widgetName, windowId, locale, deviceType } = useRequestData();
	const labels = useLabels();
	const flags = useFlags();
	const useMutipleIncentivesLink = flags['use-multiple-incentive-link'];
	const dealerOptions = useDealerOptions(
		uuid,
		locale,
		shouldRequestDealerOptions,
		widgetName
	);

	setNewRelicCustomAttribute('srpHiddenTabs', hideInformationTabs);

	const handleDealerOptionsMouseEnter = () => {
		setShouldRequestDealerOptions(true);
	};

	const showEPrice = (id) => {
		setIsDisplayingEPrice(id === uuid && id !== 'none');
	};

	const filteredIncentives = filterIncentiveIds(incentives, incentiveIdList);
	const vehicleTrackingData = getVehicleObj(uuid);
	const modifyVehicleData = {
		images,
		title
	};

	const targetLocations = getTargetLocations(
		vehicle,
		restrictInsertLocationsForVehicles
	);
	const vehiclePaymentsLocation = getVehicleLocationName(
		'vehicle-payments',
		targetLocations,
		restrictInsertLocationsForVehicles
	);
	const vehiclePricingLocation = getVehicleLocationName(
		'vehicle-pricing',
		targetLocations,
		restrictInsertLocationsForVehicles
	);
	const vehicleCtasLocation = getVehicleLocationName(
		'vehicle-ctas',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	const vehiclePaymentsLocationPrimary = getVehicleLocationName(
		'vehicle-payments-primary',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	// Special case applied new car boost style for non-simplified listing list layout
	const isApplyNewCarBoostForNonSimplifiedListView =
		showNewCarBoostPaymentsInVehicleCardData &&
		!isGridLayout &&
		!useSimplifiedListing &&
		!isSmallScreen;

	const vehiclePaymentsLocationPrimaryFragment = (
		<>
			{showNewCarBoostPaymentsInVehicleCardData &&
				!isApplyNewCarBoostForNonSimplifiedListView && (
					<WebIntegrationPlaceholder
						location="vehicle-payments-primary"
						classNames={['float-left', 'ddc-font-size-xsmall']}
						isNewCarBoost={showNewCarBoostPaymentsInVehicleCardData}
					/>
				)}
		</>
	);

	const ePriceButton = !isDisplayingEPrice ? (
		<InstantEpriceContainer
			accountId={accountId}
			buttonSize={buttonSizeEPrice}
			buttonStyle={buttonStyleEPrice}
			ePriceFontWeight={ePriceFontWeight}
			instantEpricePreventMultipleEpriceLeads={
				instantEpricePreventMultipleEpriceLeads
			}
			instantEpriceShowAllAfterLeadSubmit={
				instantEpriceShowAllAfterLeadSubmit
			}
			key="eprice"
			pageAlias={pageAlias}
			pricing={componentPricing}
			showEPrice={(id) => showEPrice(id)}
			showInstantEpriceForSharedInventory={
				showInstantEpriceForSharedInventory
			}
			userProfile={userProfile}
			vehicleId={uuid}
			vehicleAccountId={vehicleAccountId}
		/>
	) : null;

	const pricingElement = (
		<ConditionalWrapper
			condition={
				useSimplifiedListing &&
				!showInformationTabs?.toLowerCase().includes('pricing')
			}
			wrapper={(children) => (
				<>
					<Collapse in={showExpandedPricing}>
						<div
							className={[
								deviceType === 'MOBILE'
									? 'ddc-font-size-small'
									: 'ddc-font-size-xsmall',
								'mt-2'
							]}
						>
							{children}
							<button
								type="button"
								className="btn-link btn-no-decoration p-0 d-block text-muted flex-fill w-100 text-right mb-4"
								onClick={() => setShowExpandedPricing(false)}
							>
								{labels.get('HIDE_PRICING')}
							</button>
						</div>
					</Collapse>
				</>
			)}
		>
			<ErrorBoundary
				errorHandler={(error, errorInfo) => {
					setNewRelicCustomAttribute(
						'SRP ERROR',
						`ws-inv-listing error boundary: wsm-pricing-display error.\n${error}`
					);
					const newError = new Error(
						`ws-inv-listing error boundary.\n${error}`
					);
					newError.originalError = error;
					newError.originalStackTrace = errorInfo.componentStack;
					logger.error(`${newError}\n${newError.originalStackTrace}`);
				}}
			>
				{isApplyNewCarBoostForNonSimplifiedListView && (
					<div
						className="hidden vehicle-card-payments mt-3"
						data-location={vehiclePaymentsLocationPrimary}
						data-vehicle-new-car-boost={
							showNewCarBoostPaymentsInVehicleCardData
						}
					/>
				)}
				<PricingDetail
					{...{
						pricing: componentPricing,
						incentives,
						accountInfo,
						accountId,
						condition,
						isGridLayout,
						userProfile
					}}
					fetchDealerOptionsCallback={handleDealerOptionsMouseEnter}
					dealerOptions={dealerOptions}
					vehicleAccountId={vehicleAccountId}
					vehicleId={uuid}
					isDisplayingEPrice={isDisplayingEPrice}
					useMutipleIncentivesLink={useMutipleIncentivesLink}
					vehicleData={modifyVehicleData}
				/>
				{!isApplyNewCarBoostForNonSimplifiedListView && (
					<div
						className="hidden vehicle-card-payments mt-3"
						data-location={vehiclePaymentsLocation}
					/>
				)}
			</ErrorBoundary>
		</ConditionalWrapper>
	);

	// Tabs
	const tabArray = [];
	const orderedTabsData = useSimplifiedListing
		? showInformationTabs
		: tabOrder;
	const orderedTabs = orderedTabsData
		.replace(/\s/g, '')
		.toLowerCase()
		.split(',');
	const tabLabels = {
		info: labels.get(tabInfoLabel),
		specials: labels.get(tabSpecialsLabel),
		pricing: labels.get(tabPricingLabel)
	};
	const tabComponents = {
		info: (
			<React.Fragment>
				<VehicleAttributeList attributes={attributes} />
				{isTabbedLayout && !useSimplifiedListing && (
					<React.Fragment>
						{showPackages === 'true' && (
							<VehiclePackages packages={packages} link={link} />
						)}
						<Callout callouts={callouts} vehicle={vehicle} />
					</React.Fragment>
				)}
			</React.Fragment>
		),
		specials:
			showIncentives === 'true' && filteredIncentives.length ? (
				<DetailedIncentives
					incentives={filteredIncentives}
					accountInfo={accountInfo}
					incentiveTitleDisplay={incentiveTitleDisplay}
					incentiveTitleCustom={incentiveTitleCustom}
					incentiveDisplayGrouping={incentiveDisplayGrouping}
					enableAccordion={!isTabbedLayout}
					showDealerRegularIncentives={
						showDealerRegularIncentives === 'true'
					}
					showDealerConditionalIncentives={
						showDealerConditionalIncentives === 'true'
					}
					showOemNationalIncentives={
						showOemNationalIncentives === 'true'
					}
					showOemConditionalIncentives={
						showOemConditionalIncentives === 'true'
					}
					useMutipleIncentivesLink={useMutipleIncentivesLink}
					vehicleData={{
						...modifyVehicleData,
						pricing,
						condition
					}}
				/>
			) : null,
		pricing: pricingElement
	};

	orderedTabs.forEach((key) => {
		// eslint-disable-next-line no-unused-expressions
		tabComponents[key] &&
			(useSimplifiedListing
				? showInformationTabs?.toLowerCase().includes(key)
				: !hideInformationTabs?.toLowerCase().includes(key)) &&
			tabArray.push({
				eventKey: key,
				key,
				title: tabLabels[key],
				component: tabComponents[key]
			});
	});
	const useSRPWindowStickerPopover = flags['srp-window-sticker-link-change'];

	const hasInventoryButtons = inventoryButtons && inventoryButtons.length > 0;
	// prettier is causing the linter to error. I really do not care about these indents
	/* eslint-disable react/jsx-indent */
	const ctaButtons = targetLocations.has('vehicle-ctas')
		? [
				<ButtonList
					buttons={[]}
					key="inventoryButtons"
					vehicleTrackingData={undefined}
					dataLocation={vehicleCtasLocation}
				/>
		  ]
		: [
				ePriceButton,
				<ButtonList
					buttons={inventoryButtons}
					key="inventoryButtons"
					isSRP
					useSRPWindowStickerPopover={useSRPWindowStickerPopover}
					vehicleTrackingData={
						hasInventoryButtons ? vehicleTrackingData : undefined
					}
					dataLocation={vehicleCtasLocation}
				/>
		  ];
	/* eslint-enable react/jsx-indent */

	const isSpotlightedVehicle = !!vehicle.spotlightedVehicle;
	const isNewCarBoost = !!vehicle.isNewCarBoost;
	const isInTransitVehicle = isInTransit(status);

	// return for simplified listing
	if (useSimplifiedListing) {
		return (
			<div className="h-100 d-flex flex-column">
				{!showInformationTabs?.toLowerCase().includes('pricing') &&
					pricingElement}
				<HighlightedAttributeContainer
					accountInfo={accountInfo}
					hasInTransitBadge={hasInTransitBadge(callouts)}
					highlightedAttributes={highlightedAttributes}
					vehicleClassification={classification}
					isNewCarBoost={isNewCarBoost}
					isSpotlightedVehicle={isSpotlightedVehicle}
					/* TODO: `vehicle.type` will always be a string once all sites are on WIS */
					vehicleType={type === 'new' || type === 2 ? 'new' : 'used'}
				/>
				{vehiclePaymentsLocationPrimaryFragment}
				{showInformationTabs && (
					<>
						{tabArray?.length > 1 && (
							<Tabs
								className="vehicle-card-tabs tabs tabs-fill"
								id={uuid}
								onSelect={(key) =>
									trackEvent(widgetName, windowId, {
										element: `${tabLabels[key]} tab`,
										result: 'Tab displayed'
									})
								}
							>
								{tabArray.map((tab) => (
									<Tab
										eventKey={tab.eventKey}
										key={tab.key}
										title={tab.title}
									>
										{tab.component}
									</Tab>
								))}
							</Tabs>
						)}
						{tabArray?.length === 1 && tabArray[0]?.component}
					</>
				)}

				<Callout callouts={callouts} vehicle={vehicle} />
				{locationDistanceLabel && (
					<div className="mb-3">
						<LocationDistance
							label={locationDistanceLabel}
							accountId={vehicleAccountId}
							isInTransit={isInTransitVehicle}
						/>
					</div>
				)}

				<div className="d-flex flex-column justify-content-end flex-grow-1">
					<div className="vehicle-ctas">
						{ctaButtons && (
							<ErrorBoundary
								errorHandler={(error, errorInfo) => {
									setNewRelicCustomAttribute(
										'SRP ERROR',
										`ws-inv-listing error boundary: cta buttons error.\n${error}`
									);
									const newError = new Error(
										`ws-inv-listing error boundary.\n${error}`
									);
									newError.originalError = error;
									newError.originalStackTrace =
										errorInfo.componentStack;
									logger.error(
										`${newError}\n${newError.originalStackTrace}`
									);
								}}
							>
								{ctaButtons}
							</ErrorBoundary>
						)}
						{showSRPWindowSticker !== 'none' && (
							<WindowSticker vehicle={vehicle} />
						)}
					</div>
					<div
						className="hidden vehicle-card-pricing"
						data-location={vehiclePricingLocation}
					/>
				</div>
			</div>
		);
	}

	return (
		<React.Fragment>
			<HighlightedAttributeContainer
				accountInfo={accountInfo}
				hasInTransitBadge={hasInTransitBadge(callouts)}
				highlightedAttributes={highlightedAttributes}
				vehicleClassification={classification}
				isNewCarBoost={isNewCarBoost}
				isSpotlightedVehicle={isSpotlightedVehicle}
				/* TODO: `vehicle.type` will always be a string once all sites are on WIS */
				vehicleType={type === 'new' || type === 2 ? 'new' : 'used'}
			/>
			{vehiclePaymentsLocationPrimaryFragment}
			{isTabbedLayout ? (
				<div className="h-100 d-flex flex-column">
					<Tabs
						className="vehicle-card-tabs tabs tabs-fill"
						id={uuid}
						onSelect={(key) =>
							trackEvent(widgetName, windowId, {
								element: `${tabLabels[key]} tab`,
								result: 'Tab displayed'
							})
						}
					>
						{tabArray?.length > 1
							? tabArray.map((tab) => (
									/* eslint-disable react/jsx-indent */
									<Tab
										eventKey={tab.eventKey}
										key={tab.key}
										title={tab.title}
									>
										{tab.component}
									</Tab>
							  ))
							: tabArray[0]?.component}
					</Tabs>
					{/* to make sure VehicleTitle always displays pricing */}
					{hideInformationTabs?.toLowerCase().includes('pricing') && (
						<div className="d-none">{tabComponents.pricing}</div>
					)}

					{locationDistanceLabel && (
						<div className="mb-3">
							<LocationDistance
								label={locationDistanceLabel}
								accountId={vehicleAccountId}
								isInTransit={isInTransitVehicle}
							/>
						</div>
					)}

					<div className="d-flex flex-column justify-content-end flex-grow-1">
						<div className="vehicle-ctas">
							{ctaButtons && (
								<ErrorBoundary
									errorHandler={(error, errorInfo) => {
										setNewRelicCustomAttribute(
											'SRP ERROR',
											`ws-inv-listing error boundary: cta buttons error.\n${error}`
										);
										const newError = new Error(
											`ws-inv-listing error boundary.\n${error}`
										);
										newError.originalError = error;
										newError.originalStackTrace =
											errorInfo.componentStack;
										logger.error(
											`${newError}\n${newError.originalStackTrace}`
										);
									}}
								>
									{ctaButtons}
								</ErrorBoundary>
							)}
							{showSRPWindowSticker !== 'none' && (
								<WindowSticker vehicle={vehicle} />
							)}
						</div>
						<div
							className="hidden vehicle-card-pricing"
							data-location={vehiclePricingLocation}
						/>
					</div>
				</div>
			) : (
				<>
					<div className="d-md-flex">
						<div className="vehicle-card-info-packages w-100">
							{tabComponents.info}
							{showPackages === 'true' && (
								<VehiclePackages
									packages={packages}
									link={link}
								/>
							)}
						</div>
						<div className="vehicle-card-pricing w-100">
							<div className="w-100">{tabComponents.pricing}</div>
							<div className="w-100">
								{tabComponents.specials}
							</div>
							{locationDistanceLabel && useSimplifiedListing && (
								<div className="mb-3">
									<LocationDistance
										label={locationDistanceLabel}
										accountId={vehicleAccountId}
										isInTransit={isInTransitVehicle}
									/>
								</div>
							)}
							<div className="w-100">
								<ErrorBoundary
									errorHandler={(error, errorInfo) => {
										setNewRelicCustomAttribute(
											'SRP ERROR',
											`ws-inv-listing error boundary: cta buttons error.\n${error}`
										);
										const newError = new Error(
											`ws-inv-listing error boundary.\n${error}`
										);
										newError.originalError = error;
										newError.originalStackTrace =
											errorInfo.componentStack;
										logger.error(
											`${newError}\n${newError.originalStackTrace}`
										);
									}}
								>
									{ctaButtons}
									{showSRPWindowSticker !== 'none' && (
										<WindowSticker vehicle={vehicle} />
									)}
								</ErrorBoundary>
							</div>
							<div className="w-100">
								<div
									className="hidden vehicle-card-pricing mt-3"
									data-location={vehiclePricingLocation}
								/>
							</div>
						</div>
					</div>
				</>
			)}
		</React.Fragment>
	);
};

VehicleCardData.propTypes = {
	accountId: PropTypes.string.isRequired,
	accountInfo: PropTypes.shape({}),
	incentives: PropTypes.shape({}),
	isDisplayingEPrice: PropTypes.bool.isRequired,
	setIsDisplayingEPrice: PropTypes.func.isRequired,
	highlightedAttributes: PropTypes.arrayOf(PropTypes.shape({})),
	vehicle: PropTypes.shape({
		accountId: PropTypes.string,
		attributes: PropTypes.arrayOf(PropTypes.shape({})),
		bestIncentiveIds: PropTypes.arrayOf(PropTypes.string),
		callout: PropTypes.arrayOf(PropTypes.shape({})),
		classification: PropTypes.string,
		condition: PropTypes.string,
		images: PropTypes.arrayOf(PropTypes.shape({})),
		incentiveIds: PropTypes.arrayOf(PropTypes.string),
		inventoryButtons: PropTypes.arrayOf(PropTypes.shape({})),
		isNewCarBoost: PropTypes.bool,
		link: PropTypes.string,
		packages: PropTypes.arrayOf(PropTypes.string),
		pricing: PropTypes.shape({
			dprice: PropTypes.arrayOf(PropTypes.shape({})),
			dPrice: PropTypes.arrayOf(PropTypes.shape({})),
			eprice: PropTypes.arrayOf(PropTypes.shape({})),
			ePrice: PropTypes.arrayOf(PropTypes.shape({}))
		}),
		spotlightedVehicle: PropTypes.bool,
		status: PropTypes.number,
		title: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.arrayOf(PropTypes.string)
		]),
		// TODO: `vehicle.type` will always be a string once all sites are on WIS
		type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		uuid: PropTypes.string
	}).isRequired,
	showExpandedPricing: PropTypes.bool.isRequired,
	setShowExpandedPricing: PropTypes.func.isRequired,
	showNewCarBoostPaymentsInVehicleCardData: PropTypes.bool
};

VehicleCardData.defaultProps = {
	incentives: {}
};

export default VehicleCardData;
